import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
	X,
	Check,
	Loader2,
	ChevronUp,
	ChevronDown,
	ArrowUpToLine,
	Info,
	Box,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui';
import { useGetServices } from '@/services';
import { useReorderServicesDisplay } from '@/services/mutations/service';

interface ServiceDisplayOrderModalProps {
	isOpen: boolean;
	onClose: () => void;
}

/**
 * Ordena a exibição dos serviços de profissão (Psicólogo, Nutricionista,
 * Médico...) que o usuário vê nos cards do agendamento e na home. A posição
 * na lista vira a ordem e é salva de uma vez.
 */
export const ServiceDisplayOrderModal = ({
	isOpen,
	onClose,
}: ServiceDisplayOrderModalProps) => {
	const { data, isLoading, isError } = useGetServices({
		filters: { type: 'PROFESSION' },
		enabled: isOpen,
	});
	const services = useMemo(() => (Array.isArray(data) ? data : []), [data]);

	const [items, setItems] = useState<Services.Service[]>([]);
	const { mutateAsync: reorder, isPending } = useReorderServicesDisplay(() =>
		onClose(),
	);

	useEffect(() => {
		if (isOpen) setItems(services);
	}, [isOpen, services]);

	const originalIds = useMemo(() => services.map((s) => s.id), [services]);
	const isDirty = useMemo(
		() =>
			items.length !== originalIds.length ||
			items.some((s, i) => s.id !== originalIds[i]),
		[items, originalIds],
	);

	const move = (from: number, to: number) => {
		if (to < 0 || to >= items.length || from === to) return;
		setItems((prev) => {
			const next = [...prev];
			const [moved] = next.splice(from, 1);
			next.splice(to, 0, moved);
			return next;
		});
	};

	const handleSave = async () => {
		if (!isDirty || isPending) return;
		await reorder({ ordered_ids: items.map((s) => s.id) });
	};

	if (!isOpen) return null;

	const iconBtn =
		'flex h-8 w-8 items-center justify-center rounded-lg border border-stone-200 bg-white text-stone-500 transition-colors hover:border-stone-400 hover:text-stone-900 disabled:cursor-not-allowed disabled:opacity-30';

	return createPortal(
		<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
			<div
				className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"
				onClick={onClose}
			/>
			<div className="animate-in zoom-in-95 relative flex max-h-[90vh] w-full max-w-[600px] flex-col overflow-hidden rounded-[2rem] bg-white shadow-2xl duration-200">
				<div className="flex shrink-0 items-start justify-between border-b border-stone-50 px-8 py-6">
					<div>
						<h2 className="text-lg font-black text-stone-900">
							Ordem de exibição dos serviços
						</h2>
						<p className="mt-1 text-xs text-stone-400">
							Ordem em que os serviços aparecem para o usuário no
							agendamento e na home.
						</p>
					</div>
					<button
						type="button"
						onClick={onClose}
						disabled={isPending}
						className="text-stone-400 transition-colors hover:text-stone-600"
					>
						<X size={20} />
					</button>
				</div>

				<div className="flex-1 space-y-4 overflow-y-auto p-8">
					<div className="flex items-start gap-2 rounded-xl bg-stone-50 px-4 py-3 text-[11px] leading-snug text-stone-500">
						<Info size={14} className="mt-0.5 shrink-0" />
						<span>
							Só serviços de profissão entram aqui. Inativos
							aparecem na lista para manter a posição, mas o
							usuário não os vê.
						</span>
					</div>

					{isLoading && (
						<div className="flex h-40 animate-pulse items-center justify-center text-[11px] font-black uppercase tracking-widest text-stone-400">
							Carregando serviços...
						</div>
					)}
					{isError && (
						<p className="text-sm font-bold text-rose-500">
							Erro ao carregar os serviços.
						</p>
					)}
					{!isLoading && !isError && items.length === 0 && (
						<p className="py-10 text-center text-sm text-stone-400">
							Nenhum serviço de profissão cadastrado.
						</p>
					)}

					{items.length > 0 && (
						<ol className="space-y-2">
							{items.map((s, index) => (
								<li
									key={s.id}
									className="flex items-center gap-3 rounded-xl border border-stone-200 bg-white px-3 py-2.5"
								>
									<span className="w-6 shrink-0 text-center text-xs font-black text-stone-400">
										{index + 1}
									</span>
									<span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-stone-50 text-stone-400">
										{s.icon_url ? (
											<img
												src={s.icon_url}
												alt=""
												className="h-full w-full object-cover"
											/>
										) : (
											<Box size={18} />
										)}
									</span>
									<div className="min-w-0 flex-1">
										<p className="truncate text-sm font-bold text-stone-900">
											{s.service_name}
										</p>
										<p className="truncate text-[11px] text-stone-400">
											{s.professional_label ||
												'Sem rótulo'}
											{!s.active && ' · inativo'}
										</p>
									</div>
									<div className="flex shrink-0 items-center gap-1">
										<button
											type="button"
											title="Mover para o topo"
											aria-label={`Mover ${s.service_name} para o topo`}
											onClick={() => move(index, 0)}
											disabled={index === 0 || isPending}
											className={iconBtn}
										>
											<ArrowUpToLine size={14} />
										</button>
										<button
											type="button"
											title="Subir"
											aria-label={`Subir ${s.service_name}`}
											onClick={() =>
												move(index, index - 1)
											}
											disabled={index === 0 || isPending}
											className={iconBtn}
										>
											<ChevronUp size={16} />
										</button>
										<button
											type="button"
											title="Descer"
											aria-label={`Descer ${s.service_name}`}
											onClick={() =>
												move(index, index + 1)
											}
											disabled={
												index === items.length - 1 ||
												isPending
											}
											className={iconBtn}
										>
											<ChevronDown size={16} />
										</button>
									</div>
								</li>
							))}
						</ol>
					)}
				</div>

				<div className="flex shrink-0 items-center justify-end gap-6 border-t border-stone-50 p-8">
					<button
						type="button"
						onClick={onClose}
						disabled={isPending}
						className="text-sm font-bold text-stone-400 transition-colors hover:text-stone-600 disabled:opacity-50"
					>
						{isDirty ? 'Descartar e fechar' : 'Fechar'}
					</button>
					<Button
						type="button"
						onClick={handleSave}
						disabled={!isDirty || isPending}
						text={isPending ? 'Salvando...' : 'Salvar ordem'}
						className={cn(
							'min-w-[160px] rounded-full bg-black px-8 py-2.5 text-sm font-bold text-white shadow-lg hover:bg-neutral-800',
							(!isDirty || isPending) && 'opacity-50',
						)}
						startIcon={
							isPending ? (
								<Loader2 className="size-4 animate-spin" />
							) : (
								<Check className="size-4 stroke-[3]" />
							)
						}
					/>
				</div>
			</div>
		</div>,
		document.body,
	);
};
